import { useBlocker } from '@tanstack/react-router';
import { useCallback, useRef, useState } from 'react';

import { useSlideEditorContext } from '../../../context/SlideEditorContext';
import { useCanvasKeyboard } from '../../../hooks/useCanvasKeyboard';
import { CanvasStage } from '../../organisms/CanvasStage';
import { EditorTopBar } from '../../organisms/EditorTopBar';
import { ElementToolbar } from '../../organisms/ElementToolbar';
import { RightPanel } from '../../organisms/RightPanel';
import { UnsavedChangesModal } from '../../organisms/UnsavedChangesModal';

const RIGHT_DEFAULT = 320;
const RIGHT_MIN = 160;
const RIGHT_MAX = 374;

function useResizable(defaultWidth: number, min: number, max: number) {
  const [width, setWidth] = useState(defaultWidth);
  const widthRef = useRef(defaultWidth);
  widthRef.current = width;

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = widthRef.current;

      const onMouseMove = (ev: MouseEvent) => {
        const delta = startX - ev.clientX;
        setWidth(Math.min(max, Math.max(min, startWidth + delta)));
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [min, max],
  );

  return { width, onMouseDown };
}

/**
 * Template principal do editor de slides.
 * Layout: top bar → toolbar horizontal → área central + painel direito redimensionável.
 * Não realiza fetching de dados — recebe tudo via context (SlideEditorProvider).
 */
export function SlideEditorTemplate() {
  useCanvasKeyboard();

  const ctx = useSlideEditorContext();
  const right = useResizable(RIGHT_DEFAULT, RIGHT_MIN, RIGHT_MAX);

  const blocker = useBlocker({ shouldBlockFn: () => ctx.isDirty, withResolver: true });

  return (
    <>
      <div className="flex h-screen flex-col overflow-hidden bg-ctx-layout-body">
        {/* Top bar */}
        <EditorTopBar />

        {/* Element toolbar — horizontal */}
        <div className="shrink-0 border-b-hairline border-ctx-layout-border bg-ctx-layout-surface">
          <ElementToolbar />
        </div>

        {/* Main area */}
        <div className="min-h-0 flex flex-1 overflow-hidden">
          {/* Center — Canvas */}
          <main className="min-w-none flex flex-1 flex-col overflow-hidden">
            <div className="flex flex-1 items-center justify-center overflow-auto bg-ctx-layout-body p-xxs">
              <CanvasStage />
            </div>
          </main>

          {/* Right — Properties/Preview/Layers */}
          <aside
            className="relative flex shrink-0 flex-col overflow-hidden bg-ctx-layout-surface"
            style={{ width: right.width }}
          >
            {/* Resize handle — left border of the panel */}
            <button
              type="button"
              aria-label="Redimensionar painel direito"
              className="absolute inset-y-none left-none z-10 w-quark cursor-col-resize border-none bg-base-neutral-none p-none transition-colors hover:bg-ctx-interactive-primary/light focus:outline-none"
              onMouseDown={right.onMouseDown}
            />
            <RightPanel />
          </aside>
        </div>
      </div>

      {blocker.status === 'blocked' && (
        <UnsavedChangesModal
          onLeave={() => blocker.proceed?.()}
          onCancel={() => blocker.reset?.()}
        />
      )}
    </>
  );
}
